import React, { useId } from 'react';

// Architecture diagram drawn as SVG from data, so lessons stay easy to edit.
//
// { type: 'diagram', title, caption?, cols, rows,
//   nodes: [{ id, label, sub?, kind, col, row, w? }],
//   edges: [{ from, to, label?, dashed?, both?, labelAt? }] }
//
// labelAt (0–1, default 0.5) moves an edge label along its line, to keep it
// clear of crossing lines.
//
// col/row place a node on a grid (fractions such as 1.5 are fine). `w` widens
// a node in grid columns. kind: client | edge | service | db | cache | queue |
// storage | external | worker.

const COL = 240;
const ROW = 120;
const NODE_W = 160;
const NODE_H = 64;
const PAD = 24;

const KINDS = {
  client: { tag: 'Client', color: '#94a3b8' },
  edge: { tag: 'Edge', color: '#22d3ee' },
  service: { tag: 'Service', color: '#a78bfa' },
  worker: { tag: 'Worker', color: '#c084fc' },
  db: { tag: 'Database', color: '#4ade80' },
  cache: { tag: 'Cache', color: '#f87171' },
  queue: { tag: 'Queue', color: '#fbbf24' },
  storage: { tag: 'Storage', color: '#60a5fa' },
  external: { tag: 'External', color: '#f472b6' }
};

const box = (node) => {
  const w = NODE_W + ((node.w || 1) - 1) * COL;
  const cx = PAD + node.col * COL + w / 2;
  const cy = PAD + node.row * ROW + NODE_H / 2;
  return { cx, cy, w, h: NODE_H };
};

// Point where the line from a box's centre towards (tx, ty) leaves the box
const exitPoint = (b, tx, ty, gap = 6) => {
  const dx = tx - b.cx;
  const dy = ty - b.cy;
  if (dx === 0 && dy === 0) return { x: b.cx, y: b.cy };
  const scale = Math.min(
    dx === 0 ? Infinity : (b.w / 2 + gap) / Math.abs(dx),
    dy === 0 ? Infinity : (b.h / 2 + gap) / Math.abs(dy)
  );
  return { x: b.cx + dx * scale, y: b.cy + dy * scale };
};

const Diagram = ({ title, caption, cols, rows, nodes, edges }) => {
  const uid = useId().replace(/:/g, '');
  const boxes = Object.fromEntries(nodes.map((n) => [n.id, box(n)]));
  const width = PAD * 2 + (cols - 1) * COL + NODE_W;
  const height = PAD * 2 + (rows - 1) * ROW + NODE_H;

  return (
    <figure className="diagram">
      {title && <figcaption className="diagram-title">{title}</figcaption>}
      <div className="diagram-scroll">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={title || 'Architecture diagram'}
          style={{ minWidth: Math.min(width, 620) }}
        >
          <defs>
            <marker id={`arrow-${uid}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" className="diagram-arrowhead" />
            </marker>
          </defs>

          {edges.map((e, i) => {
            const a = boxes[e.from];
            const b = boxes[e.to];
            if (!a || !b) return null;
            const start = exitPoint(a, b.cx, b.cy);
            const end = exitPoint(b, a.cx, a.cy);
            const t = e.labelAt ?? 0.5;
            const mx = start.x + (end.x - start.x) * t;
            const my = start.y + (end.y - start.y) * t;
            return (
              <g key={i} className={`diagram-edge ${e.dashed ? 'is-dashed' : ''}`}>
                <line
                  x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                  markerEnd={`url(#arrow-${uid})`}
                  markerStart={e.both ? `url(#arrow-${uid})` : undefined}
                />
                {e.label && (
                  <g transform={`translate(${mx}, ${my})`}>
                    <rect x={-(e.label.length * 3.4 + 8)} y={-10} width={e.label.length * 6.8 + 16} height={20} rx={10} className="diagram-edge-bg" />
                    <text textAnchor="middle" dy="4" className="diagram-edge-label">{e.label}</text>
                  </g>
                )}
              </g>
            );
          })}

          {nodes.map((n) => {
            const b = boxes[n.id];
            const kind = KINDS[n.kind] || KINDS.service;
            const x = b.cx - b.w / 2;
            const y = b.cy - b.h / 2;
            return (
              <g key={n.id} className="diagram-node" style={{ '--node': kind.color }}>
                {n.kind === 'db' ? (
                  <>
                    <path
                      d={`M${x},${y + 8} a${b.w / 2},8 0 0,1 ${b.w},0 v${b.h - 16} a${b.w / 2},8 0 0,1 ${-b.w},0 z`}
                      className="diagram-shape"
                    />
                    <path d={`M${x},${y + 8} a${b.w / 2},8 0 0,0 ${b.w},0`} className="diagram-shape-line" />
                  </>
                ) : (
                  <rect x={x} y={y} width={b.w} height={b.h} rx={n.kind === 'queue' ? 6 : 14} className={`diagram-shape ${n.kind === 'external' ? 'is-dashed' : ''}`} />
                )}
                {n.kind === 'queue' && [0.72, 0.82, 0.92].map((f) => (
                  <line key={f} x1={x + b.w * f} y1={y + 10} x2={x + b.w * f} y2={y + b.h - 10} className="diagram-shape-line" />
                ))}
                <text x={b.cx} y={b.cy + (n.sub ? -14 : -7)} textAnchor="middle" className="diagram-tag">{kind.tag}</text>
                <text x={b.cx} y={b.cy + (n.sub ? 4 : 11)} textAnchor="middle" className="diagram-label">{n.label}</text>
                {n.sub && <text x={b.cx} y={b.cy + 20} textAnchor="middle" className="diagram-sub">{n.sub}</text>}
              </g>
            );
          })}
        </svg>
      </div>
      {caption && <p className="diagram-caption">{caption}</p>}
    </figure>
  );
};

export default Diagram;
